import { useState, useEffect, useCallback, useReducer, useRef, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  initiatePairing,
  pollPairingComplete,
} from './api';
import {
  useMqtt,
} from './mqtt';
import {
  usePlayback,
  PlaybackScreen,
  Ticker,
  useSetupHold,
} from './playback';
import {
  usePlayLog,
} from './playlog';
import { useRestHeartbeat } from './heartbeat';
import {
  loadAllAssetMeta,
  loadCachedManifest,
  storeManifestAndPreDownload,
  normalizeManifestPayload,
  normalizeAssetUrl,
  buildAssetUrlMap,
  touchAssetLastPlayed,
  registerAndCacheEmergencyAsset,
  clearOpfsAssets,
} from './cache';
import type { CachedAssetMeta } from './cache/types';
import type { AssetUrlMapEntry } from './cache/manifest-payload';
import type {
  PlayerPairingState,
  InitiatePairingResponse,
  StoredCredentials,
  PlayerCommand,
  MqttConnectionState,
} from './types';
import { CMS_API_BASE } from './types';
import type { ScheduleManifest, RecycleEvent } from '@dooh/shared';
import { resolveDeviceId } from './device-id';
import {
  useRecycleAgent,
  useInteractionReporter,
  recycleReducer,
  resetToIdle,
  isOverlayVisible,
  playlistKeyForState,
  INITIAL_RECYCLE_CONTEXT,
  RecycleOverlay,
  resolveAssetPlaybackUrl,
  resolveOpfsRecycleMediaUrl,
  decideOutcomeOnTransition,
  decideOutcomeOnIdleReset,
  INITIAL_OUTCOME_TRACKER_STATE,
  type RecycleContext,
  type OutcomeTrackerState,
} from './recycle';
import { SleepOverlay } from './sleep/SleepOverlay';
import { useSleepMode, type SleepCommandState } from './sleep/useSleepMode';
import { publishSleep, isBacklightControlled } from './playback/playable-signal';

/**
 * `useReducer(recycleReducer)` can only accept `RecycleEvent` values, but the
 * overlay's `onReset` needs to force a return to idle (e.g. the operator
 * dismisses a recovery screen) — something no agent event expresses. This
 * thin wrapper adds a `__reset` sentinel action handled outside the pure
 * `recycle-machine.ts` reducer, which stays agent-event-only.
 */
type RecycleAction = RecycleEvent | { type: '__reset' };

function wrappedRecycleReducer(ctx: RecycleContext, action: RecycleAction): RecycleContext {
  if (action.type === '__reset') return resetToIdle(ctx);
  return recycleReducer(ctx, action);
}

/** Key for storing credentials in localStorage */
const CREDS_STORAGE_KEY = 'dooh_credentials';

/** Polling interval in ms (must match server-side rate limit) */
const POLL_INTERVAL_MS = 5000;

/**
 * Load stored credentials from localStorage.
 */
function loadCredentials(): StoredCredentials | null {
  try {
    const raw = localStorage.getItem(CREDS_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredCredentials;
  } catch {
    return null;
  }
}

/**
 * Store credentials in localStorage.
 */
function storeCredentials(creds: StoredCredentials): void {
  localStorage.setItem(CREDS_STORAGE_KEY, JSON.stringify(creds));
}

/**
 * Component shown when the player is paired and connected (or connecting).
 * Uses the useMqtt hook to manage the MQTT connection lifecycle.
 * Uses the usePlayback hook to manage schedule evaluation and playback.
 */
function PlayerView({ credentials }: { credentials: StoredCredentials }) {
  // ─── Manifest state (#24) — previously dead code (never updated) ───
  const [mqttManifest, setMqttManifest] = useState<ScheduleManifest | null>(null);
  // Mirrors `mqttManifest` synchronously (GEN-128 freshness guard below). A
  // ref is used — rather than reading `mqttManifest` from closures — because
  // the guard must compare against the manifest that is ACTUALLY current at
  // call time, not a value captured when a stale async callback was created;
  // `useState` setters don't guarantee the updater runs before this function
  // returns, so a plain state read here could race.
  const mqttManifestRef = useRef<ScheduleManifest | null>(null);

  /** Sets the manifest in both state (for render) and the ref (for the guard below). */
  const setManifestState = useCallback((next: ScheduleManifest) => {
    mqttManifestRef.current = next;
    setMqttManifest(next);
  }, []);

  /**
   * Manifest freshness race guard (GEN-128). There are three writers of the
   * manifest: the MQTT `onSchedule` push, the REST boot fallback, and the
   * 5-min self-heal (which also goes through REST). MQTT push is authoritative
   * for content edits — its version changes even when validUntil barely moves
   * — so it always applies unconditionally (see the `onSchedule` handler
   * below). But the REST paths are subject to network latency: a slow REST
   * response can resolve AFTER a fresher MQTT manifest already landed, and
   * must not be allowed to clobber it with stale data. An incoming manifest
   * is considered STALE (and ignored) when the manifest already active has a
   * strictly LATER validUntil, OR is the SAME version with a validUntil that
   * is already >= the incoming one (no new information). Returns true when
   * the incoming manifest was applied.
   */
  const applyManifestIfFresher = useCallback(
    (next: ScheduleManifest): boolean => {
      const current = mqttManifestRef.current;
      if (current) {
        const currentUntil = current.validUntil ? new Date(current.validUntil).getTime() : 0;
        const nextUntil = next.validUntil ? new Date(next.validUntil).getTime() : 0;
        const sameVersion = current.version === next.version;
        const stale = currentUntil > nextUntil || (sameVersion && currentUntil >= nextUntil);
        if (stale) return false;
      }
      setManifestState(next);
      return true;
    },
    [setManifestState],
  );

  const [emergencyCommand, setEmergencyCommand] = useState<
    { type: 'EMERGENCY'; assetId: string; duration: number; expiresAt: string; assetType?: 'image' | 'video' }
    | { type: 'CLEAR' }
    | null
  >(null);
  // Ad-hoc sleep override from SLEEP/WAKE MQTT commands — sticky until the
  // opposite command arrives or the page reloads; wins over the manifest's
  // scheduled sleep window (see useSleepMode).
  const [sleepCommand, setSleepCommand] = useState<SleepCommandState | null>(null);
  const [assetMetaMap, setAssetMetaMap] = useState<Map<string, CachedAssetMeta>>(new Map());

  // Proof-of-play gate for images (#173). onAssetStart fires when an item
  // becomes current — before we know whether it actually rendered. For images
  // we hold the assetId here and only emit the billable impression once the
  // <img> fires `load` (handleImageLoad); a failed render clears it without
  // billing (handleImageError). null = no image impression awaiting confirmation.
  const pendingImageImpressionRef = useRef<string | null>(null);

  // Reload asset metadata from IndexedDB into React state so PlaybackScreen
  // can resolve playback URLs for newly registered/downloaded assets.
  const refreshAssetMetaMap = useCallback(async () => {
    const metas = await loadAllAssetMeta();
    const map = new Map<string, CachedAssetMeta>();
    for (const m of metas) {
      map.set(m.assetId, m);
    }
    setAssetMetaMap(map);
  }, []);

  // Make emergency creative renderable even when it isn't cached (#71):
  // register the asset's remote URL immediately (so PlaybackScreen can stream
  // it), then download + checksum-validate it in the background.
  const cacheEmergencyAsset = useCallback(
    async (assetId: string, asset: { url: string; checksum: string; fileSize: number; type: 'image' | 'video' }) => {
      try {
        await registerAndCacheEmergencyAsset(
          assetId,
          { ...asset, url: normalizeAssetUrl(asset.url, CMS_API_BASE) },
          // Refresh state as soon as the provisional meta is registered so
          // the emergency renders from the remote URL without waiting for
          // the download.
          refreshAssetMetaMap,
        );
        // Refresh again after download so playback switches to the verified
        // cached copy (offline-capable).
        await refreshAssetMetaMap();
      } catch (err) {
        console.error('[Player] Failed to cache emergency asset (non-fatal):', err);
      }
    },
    [refreshAssetMetaMap],
  );

  // ─── Asset pre-download + assetMetaMap reload (#69) ──────────────
  // Shared by the MQTT onSchedule handler and the REST boot fallback.
  // Pre-download errors are NON-FATAL: playback continues with whatever is
  // cached. The manifest must already be set in state before calling this.
  const preDownloadFromPayload = useCallback(
    async (
      manifest: ScheduleManifest,
      assetsMap: Map<string, AssetUrlMapEntry>,
    ) => {
      try {
        const stats = await storeManifestAndPreDownload(
          manifest,
          credentials.screenId,
          assetsMap,
          [], // fallbackAssetIds — fallback playlist wiring is #86
        );
        console.log(
          `[Player] Pre-download: ${stats.downloaded} downloaded, ${stats.skipped} cached, ${stats.failed} failed`,
        );

        // Reload asset metadata into state so PlaybackScreen can render.
        // preDownloadAssets writes assetMeta records to IndexedDB; we refresh
        // the React state map that PlaybackScreen reads from.
        await refreshAssetMetaMap();
      } catch (err) {
        // Non-fatal: playback proceeds with whatever is cached.
        console.error('[Player] Pre-download failed (non-fatal):', err);
      }
    },
    [credentials.screenId, refreshAssetMetaMap],
  );

  // Fetch the manifest via REST and pre-download assets (#69, #76).
  // Used as a boot fallback when MQTT is unavailable but a cached manifest
  // exists with no cached assets (fresh OPFS), and by the self-heal effect
  // below. Guarded by applyManifestIfFresher (GEN-128) — this REST response
  // is slow enough to race a fresher MQTT push, so a stale result is dropped
  // (and its pre-download skipped) rather than applied.
  const fetchManifestAndPreDownload = useCallback(async () => {
    const response = await fetch(
      `${CMS_API_BASE}/api/screens/${credentials.screenId}/manifest`,
      { headers: { Authorization: `Bearer ${credentials.jwt}` } },
    );
    if (!response.ok) {
      throw new Error(`REST manifest fetch failed: HTTP ${response.status}`);
    }
    const data = await response.json();
    const { manifest, assets } = normalizeManifestPayload(data);
    if (!applyManifestIfFresher(manifest)) {
      console.log('[Player] REST manifest fetch was stale — a fresher manifest is already active, ignoring');
      return;
    }
    await preDownloadFromPayload(
      manifest,
      buildAssetUrlMap(assets, CMS_API_BASE),
    );
  }, [credentials.screenId, credentials.jwt, preDownloadFromPayload, applyManifestIfFresher]);

  // Load cached manifest and asset metadata on mount (#24)
  useEffect(() => {
    async function loadCachedData() {
      try {
        // Load cached manifest so playback works offline immediately
        const cached = await loadCachedManifest(credentials.screenId);
        if (cached?.manifest) {
          console.log('[Player] Loaded cached manifest v' + cached.manifest.version);
          // Initial hydration, not a competing writer — set directly (via
          // setManifestState, so the freshness-guard ref stays in sync) rather
          // than through applyManifestIfFresher.
          setManifestState(cached.manifest);
        }

        // Load cached asset metadata
        const metas = await loadAllAssetMeta();
        const map = new Map<string, CachedAssetMeta>();
        for (const m of metas) {
          map.set(m.assetId, m);
        }
        setAssetMetaMap(map);

        // Boot fallback (#69, #76): fetch the manifest via REST when either
        // nothing is cached (fresh device — MQTT may be unavailable, and
        // without this the player sits in fallback mode forever) or a cached
        // manifest exists but assets are missing (fresh OPFS). Non-blocking —
        // playback starts from the cached manifest when one exists.
        if (!cached?.manifest || map.size === 0) {
          fetchManifestAndPreDownload().catch((err) => {
            console.warn('[Player] REST manifest fetch failed:', err);
          });
        }
      } catch (err) {
        console.error('[Player] Failed to load cached data:', err);
      }
    }
    loadCachedData();
  }, [credentials.screenId, fetchManifestAndPreDownload, setManifestState]);

  // Manifest self-heal (#GEN-126): the compiled schedule manifest carries a
  // validity window (validUntil, ~24h). When it lapses the schedule evaluator
  // rejects the whole manifest and the screen goes dark ("No content
  // available") until something republishes a fresh window. That has depended
  // on a server-side recompile cron, which is fragile (host crontabs get
  // clobbered). This makes the player self-recover: every few minutes, if the
  // current manifest is missing, expired, or within a margin of expiry,
  // re-fetch via REST — the CMS route recompiles a fresh window. Independent
  // of MQTT and any server cron.
  useEffect(() => {
    const CHECK_MS = 5 * 60 * 1000; // check cadence
    const MARGIN_MS = 30 * 60 * 1000; // refresh this long before validUntil
    const maybeRefresh = () => {
      const until = mqttManifest?.validUntil
        ? new Date(mqttManifest.validUntil).getTime()
        : 0;
      if (!until || Number.isNaN(until) || Date.now() >= until - MARGIN_MS) {
        fetchManifestAndPreDownload().catch((err) => {
          console.warn('[Player] manifest self-heal re-fetch failed:', err);
        });
      }
    };
    const timer = setInterval(maybeRefresh, CHECK_MS);
    return () => clearInterval(timer);
  }, [mqttManifest?.validUntil, fetchManifestAndPreDownload]);

  // Build asset info map for usePlayback.
  // Video duration is null until a metadata probe is implemented (#47);
  // the schedule evaluator falls back to slot.loopDuration for null durations,
  // and VideoPlayer drives advancement via onEnded for single-video slots.
  const assets = useMemo(() => {
    const map = new Map<string, { type: 'image' | 'video'; duration: number | null }>();
    for (const [id, meta] of assetMetaMap) {
      map.set(id, {
        type: meta.assetType,
        // Native duration ships with the manifest assets payload (#47) and is
        // persisted in the cached meta; null for images and legacy records.
        duration: meta.duration ?? null,
      });
    }
    return map;
  }, [assetMetaMap]);

  const {
    connectionState,
    publish: mqttPublish,
  } = useMqtt({
    credentials,
    onCommand: (command: PlayerCommand) => {
      console.log('[Player] Command received:', command.type, command);

      // Send acknowledgment for all commands
      if (command.commandId) {
        const ackTopic = `dooh/${credentials.organizationId}/${credentials.deviceId}/ack`;
        const ackPayload = JSON.stringify({
          commandId: command.commandId,
          type: command.type,
          status: 'received',
          timestamp: new Date().toISOString(),
        });
        mqttPublish(ackTopic, ackPayload, 1);
      }

      if (command.type === 'EMERGENCY' && command.assetId && command.duration && command.expiresAt) {
        setEmergencyCommand({
          type: 'EMERGENCY',
          assetId: command.assetId,
          duration: command.duration,
          expiresAt: command.expiresAt,
          // Asset type from the command payload (#71) — the local metadata
          // map is empty for creative this device hasn't cached.
          assetType: command.asset?.type,
        });
        // Fetch + cache the emergency creative if the command carries its
        // metadata and it isn't already cached (#71). Non-blocking.
        if (command.asset) {
          cacheEmergencyAsset(command.assetId, command.asset);
        }
      } else if (command.type === 'CLEAR') {
        setEmergencyCommand({ type: 'CLEAR' });
      } else if (command.type === 'SLEEP') {
        console.log('[Player] SLEEP command received', command.variant ? `(variant: ${command.variant})` : '');
        setSleepCommand({ mode: 'sleep', variant: command.variant });
      } else if (command.type === 'WAKE') {
        console.log('[Player] WAKE command received');
        setSleepCommand({ mode: 'wake' });
      } else if (command.type === 'RELOAD_SCHEDULE') {
        // Re-evaluate schedule (manifest may have been updated)
        playback.reevaluate();
      } else if (command.type === 'REBOOT') {
        console.log('[Player] REBOOT command received — reloading page');
        // Reload the page (equivalent to rebooting the player)
        // In production on Raspberry Pi, this would trigger a full reboot
        // via a systemd service or OS-level script
        window.location.reload();
      } else if (command.type === 'CLEAR_CACHE') {
        console.log('[Player] CLEAR_CACHE command received — clearing caches and reloading');
        // Clear all storage and reload
        async function clearCacheAndReload() {
          try {
            // Clear IndexedDB
            const dbs = await indexedDB.databases();
            for (const db of dbs) {
              if (db.name) {
                indexedDB.deleteDatabase(db.name);
              }
            }
            // Clear Cache Storage
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
            // Clear OPFS asset files (#193) — cached videos/images live in the
            // origin-private file system, which indexedDB/caches/localStorage
            // clears do NOT touch, so a stale creative would otherwise survive.
            await clearOpfsAssets();
            // Clear localStorage (except credentials)
            const creds = localStorage.getItem('dooh_credentials');
            const deviceId = localStorage.getItem('dooh_deviceId');
            localStorage.clear();
            if (creds) localStorage.setItem('dooh_credentials', creds);
            if (deviceId) localStorage.setItem('dooh_deviceId', deviceId);
            console.log('[Player] Cache cleared successfully, reloading');
            window.location.reload();
          } catch (err) {
            console.error('[Player] Failed to clear cache:', err);
            // Reload anyway
            window.location.reload();
          }
        }
        clearCacheAndReload();
      }
    },
    onStateChange: (state: MqttConnectionState) => {
      console.log('[Player] MQTT state:', state);
    },
    onError: (error: Error) => {
      console.error('[Player] MQTT error:', error.message);
    },
    onSchedule: async (rawPayload: unknown) => {
      // Schedule manifest received via MQTT (#24) — store, activate, and
      // pre-download assets so OPFS is populated and PlaybackScreen can render.
      // Previously only storeManifest() was called, so OPFS stayed empty and
      // PlaybackScreen showed "Asset metadata not found" (#69).
      console.log('[Player] Schedule manifest received via MQTT');
      const { manifest, assets } = normalizeManifestPayload(rawPayload);
      // Set manifest in state FIRST so the schedule evaluator runs regardless
      // of whether pre-download succeeds (offline-first). Applied
      // UNCONDITIONALLY (via setManifestState, not the REST paths' freshness
      // guard) — server push is authoritative for content edits; its version
      // changes on every edit even when validUntil barely moves, so it must
      // always win over whatever the REST paths currently hold (GEN-128).
      setManifestState(manifest);
      await preDownloadFromPayload(
        manifest,
        buildAssetUrlMap(assets, CMS_API_BASE),
      );
    },
  });

  // ─── Play log tracker ────────────────────────────────────────────
  const playLog = usePlayLog({
    deviceId: credentials.deviceId,
    screenId: credentials.screenId,
    jwt: credentials.jwt,
  });

  // ─── REST heartbeat (authoritative online signal) ─────────────────
  // The CMS marks a screen ONLINE only via POST /api/heartbeat (GEN-128: the
  // MQTT telemetry topic this used to also publish to had no server-side
  // consumer and was removed). Runs independently of the MQTT connection
  // state so the screen shows online even if MQTT drops.
  const restHeartbeat = useRestHeartbeat({
    deviceId: credentials.deviceId,
    jwt: credentials.jwt,
  });

  // ─── Recycle overlay reducer (declared before usePlayback so its
  // visibility can be threaded in as `suspended`, freezing the schedule
  // under the overlay) ────────────────────────────────────────────────
  const [recycleCtx, dispatchRecycle] = useReducer(wrappedRecycleReducer, INITIAL_RECYCLE_CONTEXT);
  const recycleOverlayVisible = isOverlayVisible(recycleCtx.state);

  // Hold-to-open on-screen Wi-Fi setup (10s full-screen hold). The device
  // gate reads window.__doohSetupRequested over CDP and starts SoftAP.
  useSetupHold(true);

  // ─── Sleep mode ────────────────────────────────────────────────────
  // Scheduled (manifest sleep window) or ad-hoc (SLEEP/WAKE MQTT command).
  // While active: playback is suspended below, the stage is dimmed with a
  // CSS brightness filter, and the sleep overlay covers the screen.
  const sleep = useSleepMode({ manifest: mqttManifest, command: sleepCommand });

  // Publish the sleep signal so the device backlight daemon can drop the
  // panel to the target brightness over CDP (same pattern as __doohPlayable).
  useEffect(() => {
    publishSleep(
      sleep.active
        ? { active: true, brightness: sleep.brightness, at: new Date().toISOString() }
        : null,
    );
  }, [sleep.active, sleep.brightness]);

  // ─── Playback engine ─────────────────────────────────────────────
  const playback = usePlayback({
    screenId: credentials.screenId,
    assets,
    manifestOverride: mqttManifest,
    emergencyCommand,
    suspended: recycleOverlayVisible || sleep.active,
    onAssetStart: (assetId) => {
      console.log('[Player] Asset started:', assetId);
      restHeartbeat.updatePlayback({ currentAssetId: assetId });
      // Proof-of-play must reflect what actually rendered (#173). For images,
      // defer the billable impression until the <img> fires `load`
      // (handleImageLoad) and suppress it on error (handleImageError) so an
      // expired/broken presigned URL never bills content that never displayed.
      // Videos keep immediate logging — VideoPlayer drives its own error/stall
      // recovery and is billed on start as before.
      if (assetMetaMap.get(assetId)?.assetType === 'image') {
        pendingImageImpressionRef.current = assetId;
      } else {
        playLog.onAssetStart(assetId);
      }
      // Update LRU tracking for eviction (#41) — previously dead code.
      touchAssetLastPlayed(assetId).catch((err) => {
        console.warn('[Player] Failed to update LRU tracking:', err);
      });
    },
    onAssetEnd: (assetId) => {
      console.log('[Player] Asset ended:', assetId);
      playLog.onAssetEnd(assetId);
    },
    onEvaluation: (state) => {
      restHeartbeat.updatePlayback({
        scheduleVersion: state.manifestVersion,
      });
    },
  });

  // ─── Image proof-of-play gate + error recovery (#173) ─────────────
  // Emit the deferred image impression only once the <img> actually renders.
  const handleImageLoad = useCallback(() => {
    const assetId = pendingImageImpressionRef.current;
    if (!assetId) return;
    pendingImageImpressionRef.current = null;
    playLog.onAssetStart(assetId);
  }, [playLog]);

  // A failed image render must not bill (drop the pending impression) and must
  // not sit as a black frame for its full slot duration — advance immediately.
  const handleImageError = useCallback(() => {
    const assetId = pendingImageImpressionRef.current;
    pendingImageImpressionRef.current = null;
    if (assetId) {
      console.warn('[Player] Image failed to render — suppressing impression:', assetId);
    }
    playback.advanceToNext();
  }, [playback]);

  // ─── Recycle-kiosk agent + overlay (Task 10) ─────────────────────
  // Only instantiated within PlayerView, which only renders once paired —
  // dormant screens with no recycle agent present simply see a WS connection
  // that never opens (useRecycleAgent handles that silently).
  const interactionReporter = useInteractionReporter({
    deviceId: credentials.deviceId,
    screenId: credentials.screenId,
    jwt: credentials.jwt,
  });
  /**
   * Bookkeeping for terminal-outcome reporting (see `interaction-outcomes.ts`
   * for the full rationale — obstruction is a recovery state, not terminal;
   * it's only reported once it resolves to a real outcome or the machine
   * resets to idle with no result in between). Held in a ref since it's
   * mutated from effects and must not trigger re-renders.
   */
  const outcomeTrackerRef = useRef<OutcomeTrackerState>(INITIAL_OUTCOME_TRACKER_STATE);

  const { agentOnline, health, sendCommand } = useRecycleAgent({
    onEvent: (event) => {
      if (event.type === 'scan') {
        // Firewall (B7 carry): close the in-flight impression honestly
        // BEFORE suppressing further billable plays — never suppress
        // without closing first.
        playLog.closeActiveForInteraction();
        playLog.setInteractionActive(true);
      }
      dispatchRecycle(event);
    },
  });

  // Tracks whether the recycle agent WS has ever connected on this screen —
  // distinguishes a screen that HAD an agent connection drop ('down') from a
  // plain-signage screen where no recycle agent has ever been present
  // ('absent'). Ref (not state): read only inside the effect below.
  const everConnectedRecycleRef = useRef(false);
  // Record that the recycle agent has connected at least once. Kept in an
  // effect rather than a bare assignment during render so the render pass stays
  // free of side effects (a mutation during render is unsafe under React's
  // concurrent/StrictMode double-invoked renders). Declared before the
  // heartbeat effect below so the flag is already set when that effect reads it.
  useEffect(() => {
    if (agentOnline) everConnectedRecycleRef.current = true;
  }, [agentOnline]);

  // Publish the recycle health block into the REST heartbeat whenever the
  // agent connection state or latest health snapshot changes (#B12 —
  // heartbeatInputSchema.recycle was accepted by the CMS but never produced
  // by the player). REST is the only channel here (GEN-128) — the MQTT
  // telemetry topic this used to also publish to had no server-side consumer
  // and was removed.
  useEffect(() => {
    const recycle = {
      agent: agentOnline
        ? ('up' as const)
        : everConnectedRecycleRef.current
          ? ('down' as const)
          : ('absent' as const),
      servo: health?.servo,
      dropSensor: health?.dropSensor,
      nfc: health?.nfc,
      binFillPercent: health?.binFillPercent ?? null,
    };
    restHeartbeat.updateRecycle({
      ...recycle,
      state: health?.state,
      outOfServiceReason: health?.outOfServiceReason,
    });
  }, [agentOnline, health, restHeartbeat]);

  // Report terminal outcomes exactly once per (txnId, state) pair.
  useEffect(() => {
    const { state, txnId, barcode } = recycleCtx;
    const decision = decideOutcomeOnTransition(outcomeTrackerRef.current, { state, txnId, barcode });
    outcomeTrackerRef.current = decision.next;
    if (decision.report) {
      interactionReporter.report({
        ...decision.report,
        at: new Date().toISOString(),
      });
    }
  }, [recycleCtx.state, recycleCtx.txnId, recycleCtx.barcode, interactionReporter]);

  // Resume playback/billing when the overlay returns to idle. Also flushes
  // a terminal OBSTRUCTED report if the machine reset from `obstructed`
  // without ever reaching a result (operator cleared the jam and the agent
  // simply went idle — its recovery-success path emits no `result` event).
  useEffect(() => {
    if (recycleCtx.state === 'idle') {
      playLog.setInteractionActive(false);
      playback.reevaluate();

      const decision = decideOutcomeOnIdleReset(outcomeTrackerRef.current);
      outcomeTrackerRef.current = decision.next;
      if (decision.report) {
        interactionReporter.report({
          ...decision.report,
          at: new Date().toISOString(),
        });
      }
    }
  }, [recycleCtx.state, interactionReporter]);

  // ─── Recycle overlay media resolution ────────────────────────────
  // resolveMedia (passed to RecycleOverlay) must be synchronous; OPFS-backed
  // assets need an async blob-URL read, so those are resolved in the
  // background here and cached into recycleResolvedUrls once ready.
  const [recycleResolvedUrls, setRecycleResolvedUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const candidateKeys = [playlistKeyForState(recycleCtx.state), recycleCtx.playlistOverride].filter(
      (key): key is string => Boolean(key),
    );
    for (const key of candidateKeys) {
      void resolveOpfsRecycleMediaUrl(key, assetMetaMap, recycleResolvedUrls, (resolvedKey, url) => {
        setRecycleResolvedUrls((prev) => {
          if (prev.get(resolvedKey) === url) return prev;
          const next = new Map(prev);
          next.set(resolvedKey, url);
          return next;
        });
      });
    }
  }, [recycleCtx.state, recycleCtx.playlistOverride, assetMetaMap, recycleResolvedUrls]);

  const resolveRecycleMedia = useCallback(
    (keyOrAssetId: string) => resolveAssetPlaybackUrl(keyOrAssetId, assetMetaMap, recycleResolvedUrls),
    [assetMetaMap, recycleResolvedUrls],
  );

  // ─── Playback always renders when paired (#23) ─────────────────────
  // Playback is decoupled from MQTT connection state. Offline-first means
  // the player shows cached content even when MQTT is disconnected.
  // Connection status is shown as a small HUD overlay, not a full-screen
  // replacement of the content.
  // #212: ticker follows the CONTENT — the active slot's playlist ticker wins;
  // otherwise fall back to the manifest-level ticker (the default/fallback
  // playlist's), which also covers fallback playback mode.
  const ticker = playback.state.slot?.ticker ?? mqttManifest?.ticker;

  // Sleep mode dims the panel two ways: the on-device backlight daemon when
  // present (true power saving), else a CSS brightness filter approximation.
  // Never both — that would double-dim.
  return (
    <div
      className="relative flex h-screen w-screen flex-col overflow-hidden bg-black"
      style={{
        filter: sleep.active && !isBacklightControlled() ? `brightness(${sleep.brightness})` : undefined,
        transition: 'filter 0.7s ease-in-out',
      }}
    >
      {/* Stage: fills everything above the ticker strip */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <PlaybackScreen
          item={playback.currentItem}
          mode={playback.state.mode}
          assetMetaMap={assetMetaMap}
          onVideoEnded={playback.advanceToNext}
          onImageError={handleImageError}
          onImageLoad={handleImageLoad}
          paused={recycleOverlayVisible || sleep.active}
        />
      </div>

      {/* Bottom ticker (#212) — standing message strip, e.g. SCAN ITEM TO RECYCLE */}
      {ticker?.enabled && <Ticker config={ticker} />}

      <RecycleOverlay
        ctx={recycleCtx}
        resolveMedia={resolveRecycleMedia}
        onScreenDone={(txnId) =>
          sendCommand({ v: 1, type: 'ack_screen_done', txnId: txnId ?? null, ts: new Date().toISOString(), data: {} })
        }
        onOperatorClear={(txnId) =>
          sendCommand({
            v: 1,
            type: 'operator_clear_attempt',
            txnId: txnId ?? null,
            ts: new Date().toISOString(),
            data: {},
          })
        }
        onReset={() => dispatchRecycle({ type: '__reset' })}
        agentOnline={agentOnline}
      />

      {/* Sleep overlay — renders above signage (and any recycle screen): a
          sleeping device must never show interaction UI. */}
      {sleep.active && (
        <SleepOverlay
          variant={sleep.variant}
          wakeAt={sleep.wakeAt}
          wakeProgress={sleep.progress}
        />
      )}

      {/* Recycle agent offline badge — idle-only so it never blocks the
          overlay's interaction screens or the underlying signage content. */}
      {!agentOnline && !recycleOverlayVisible && (
        <div className="absolute bottom-4 left-4 z-50 pointer-events-none rounded bg-black/70 px-2 py-1 text-xs text-amber-400">
          Recycle agent offline
        </div>
      )}

      {/* Connection status overlay (non-blocking) */}
      {connectionState !== 'connected' && (
        <div className="absolute bottom-4 right-4 z-50 pointer-events-none">
          <div className="flex items-center gap-2 rounded-lg bg-black/70 px-3 py-1.5 backdrop-blur">
            {connectionState === 'connecting' && (
              <>
                <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-xs text-blue-400">Connecting</span>
              </>
            )}
            {connectionState === 'reconnecting' && (
              <>
                <div className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-xs text-yellow-400">Reconnecting</span>
              </>
            )}
            {connectionState === 'disconnected' && (
              <>
                <div className="h-2 w-2 rounded-full bg-red-500" />
                <span className="text-xs text-red-400">Offline</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [pairingState, setPairingState] = useState<PlayerPairingState>('unpaired');
  const [pairingData, setPairingData] = useState<InitiatePairingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const deviceIdRef = useRef<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Start the pairing process: initiate and begin polling.
   */
  const startPairing = useCallback(async () => {
    setError(null);
    try {
      deviceIdRef.current ??= resolveDeviceId(window.location.search, localStorage);
      const result = await initiatePairing(deviceIdRef.current);
      setPairingData(result);
      setPairingState('pairing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate pairing');
      if (!deviceIdRef.current) return;
      // Retry after a delay
      setTimeout(() => startPairing(), 10000);
    }
  }, []);

  /**
   * Poll for pairing completion.
   */
  const pollForCompletion = useCallback(async () => {
    if (!pairingData?.deviceCode) return;

    try {
      const result = await pollPairingComplete(pairingData.deviceCode);

      if (result.status === 'confirmed' && result.jwt) {
        // Stop polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }

        // Store credentials
        const creds: StoredCredentials = {
          jwt: result.jwt,
          screenId: result.screenId ?? '',
          deviceId: result.deviceId ?? deviceIdRef.current ?? '',
          organizationId: result.organizationId ?? '',
        };
        storeCredentials(creds);
        setPairingState('paired');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Polling failed';
      // Don't stop polling on transient errors, but show message
      if (msg.includes('expired') || msg.includes('already been used')) {
        setError(msg);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        // Restart pairing
        setTimeout(() => startPairing(), 2000);
      }
    }
  }, [pairingData, startPairing]);

  // Check for stored credentials on mount
  useEffect(() => {
    const creds = loadCredentials();
    if (creds?.jwt) {
      setPairingState('paired');
      return;
    }
    // No credentials, start pairing
    startPairing();
  }, [startPairing]);

  // Recover from a paired state with missing/corrupt credentials (#196).
  // loadCredentials() can return null after we've transitioned to 'paired'
  // (localStorage cleared or corrupted). Handling this in render would either
  // call setState during render or leave a stale, non-functional QR on screen
  // with polling stopped. Instead, reset to unpaired AND restart pairing here
  // so a fresh QR + poll loop is established.
  useEffect(() => {
    if (pairingState === 'paired' && !loadCredentials()) {
      setPairingState('unpaired');
      startPairing();
    }
  }, [pairingState, startPairing]);

  // Start polling when pairing data is available
  useEffect(() => {
    if (pairingState !== 'pairing' || !pairingData?.deviceCode) return;

    // Initial poll
    pollForCompletion();

    // Set up interval
    pollingRef.current = setInterval(pollForCompletion, POLL_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [pairingState, pairingData, pollForCompletion]);

  // ─── Paired state: use MQTT connection ───────────────────────────
  if (pairingState === 'paired') {
    const creds = loadCredentials();
    if (creds) {
      return <PlayerView credentials={creds} />;
    }
    // Credentials vanished after pairing — the recovery effect above resets to
    // unpaired and restarts pairing. Fall through to the pairing screen in the
    // meantime; never call setState during render.
  }

  // ─── Unpaired / Pairing state: show QR code ───────────────────────
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-black text-white p-8">
      <div className="text-center max-w-md">
        <h1 className="text-3xl font-bold tracking-tight mb-2">DOOH Player</h1>
        <p className="text-gray-400 mb-8">
          Scan the QR code to pair this device
        </p>

        {error && !pairingData && (
          <div className="mb-4 rounded-md bg-red-900/50 p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {pairingData ? (
          <>
            {/* QR Code encoding verificationUri */}
            <div className="mx-auto mb-6 flex items-center justify-center rounded-xl bg-white p-4">
              <QRCodeSVG
                value={pairingData.verificationUri}
                size={256}
                level="M"
                aria-label="QR code for device pairing"
              />
            </div>

            {/* Short code display */}
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                Pairing Code
              </p>
              <p className="text-4xl font-mono font-bold tracking-[0.3em] text-white">
                {pairingData.userCode}
              </p>
            </div>

            <p className="text-xs text-gray-600">
              Code expires in {Math.ceil(pairingData.expiresIn / 60)} minutes
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            <p className="text-sm text-gray-400">Initializing pairing...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
